const DB_NAME = "surf-job-queue";
const DB_VERSION = 1;
const STORE = "queue";
const LS_PREFIX = "surf.jobQueue.";
const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
function nowIso() {
    return new Date().toISOString();
}
function lsKey(userId) {
    return `${LS_PREFIX}${userId}`;
}
function pruneOldJobs(jobs) {
    const cutoff = Date.now() - RETENTION_MS;
    return jobs.filter((job) => {
        if (job.status !== "applied" && job.status !== "failed")
            return true;
        const ts = Date.parse(job.appliedAt ?? job.updatedAt);
        return Number.isFinite(ts) && ts >= cutoff;
    });
}
async function openDb() {
    if (typeof indexedDB === "undefined")
        return null;
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    });
}
async function readFromIdb(userId) {
    const db = await openDb();
    if (!db)
        return null;
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(userId);
        req.onsuccess = () => {
            const value = req.result;
            resolve(Array.isArray(value) ? value : []);
        };
        req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
    });
}
async function writeToIdb(userId, jobs) {
    const db = await openDb();
    if (!db)
        throw new Error("IndexedDB unavailable");
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(jobs, userId);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("IndexedDB write failed"));
    });
}
function readFromLocalStorage(userId) {
    if (typeof localStorage === "undefined")
        return [];
    try {
        const raw = localStorage.getItem(lsKey(userId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
function writeToLocalStorage(userId, jobs) {
    if (typeof localStorage === "undefined")
        return;
    localStorage.setItem(lsKey(userId), JSON.stringify(jobs));
}
export async function loadJobQueue(userId) {
    try {
        const fromIdb = await readFromIdb(userId);
        if (fromIdb)
            return pruneOldJobs(fromIdb);
    }
    catch {
        /* fallback below */
    }
    return pruneOldJobs(readFromLocalStorage(userId));
}
export async function saveJobQueue(userId, jobs) {
    const pruned = pruneOldJobs(jobs);
    try {
        await writeToIdb(userId, pruned);
    }
    catch {
        writeToLocalStorage(userId, pruned);
        return;
    }
    writeToLocalStorage(userId, pruned);
}
export function createQueuedJob(partial) {
    const ts = nowIso();
    return {
        ...partial,
        status: partial.status ?? "pending",
        submittedAt: partial.submittedAt ?? ts,
        updatedAt: partial.updatedAt ?? ts,
    };
}
export function updateQueuedJob(jobs, jobId, patch) {
    return jobs.map((job) => job.jobId === jobId ? { ...job, ...patch, updatedAt: nowIso() } : job);
}
export function removeQueuedJob(jobs, jobId) {
    return jobs.filter((job) => job.jobId !== jobId);
}
export function listQueuedJobs(jobs, deckId) {
    const sorted = [...jobs].sort((a, b) => Date.parse(b.submittedAt) - Date.parse(a.submittedAt));
    if (!deckId)
        return sorted;
    return sorted.filter((job) => job.deckId === deckId);
}
export function markJobApplied(jobs, jobId) {
    const ts = nowIso();
    return jobs.map((job) => job.jobId === jobId
        ? { ...job, status: "applied", appliedAt: ts, updatedAt: ts }
        : job);
}
export function isTerminalJobStatus(status) {
    return status === "completed" || status === "failed" || status === "applied";
}
export function isActiveJobStatus(status) {
    return status === "pending" || status === "running";
}
